import React, { useMemo, useState } from "react";
import { Select } from "antd";
import dayjs from "dayjs";
import { formatToINR } from "../../../../helpers/utility";
import bedIcon from "../../../../assets/receptionistIcons/ipBilling/bed.svg";
import styles from "./BillingIp.module.css";

const BED_COLUMNS = [
  "Start date",
  "End date",
  "Bed days",
  "Room",
  "Type",
  "Status",
  "Total",
];

const BedsIp = ({ billData }) => {
  const beds = billData?.data?.beds_details?.beds || [];
  const [selectedMonth, setSelectedMonth] = useState(null);

  const monthOptions = useMemo(() => {
    const months = new Map();
    beds.forEach((bed) => {
      if (!bed.start_date) return;
      const key = dayjs(bed.start_date).format("YYYY-MM");
      if (!months.has(key)) {
        months.set(key, {
          value: key,
          label: dayjs(bed.start_date).format("MMM YYYY"),
        });
      }
    });
    return Array.from(months.values());
  }, [beds]);

  const filteredBeds = useMemo(() => {
    if (!selectedMonth) return beds;
    return beds.filter(
      (bed) => dayjs(bed.start_date).format("YYYY-MM") === selectedMonth
    );
  }, [beds, selectedMonth]);

  const bedTotal = filteredBeds.reduce(
    (sum, bed) => sum + Number(bed.total_price || 0),
    0
  );

  return (
    <section className={styles.bedCard}>
      <header className={styles.bedCardHeader}>
        <div className={styles.bedCardHeaderLeft}>
          <img src={bedIcon} alt="" className={styles.bedCardIcon} />
          <div>
            <h3 className={styles.sectionCardTitle}>Bed allocation</h3>
            <p className={styles.bedSubtitle}>
              {filteredBeds.length} transactions · {formatToINR(bedTotal)}
            </p>
          </div>
        </div>
        {monthOptions.length > 0 && (
          <Select
            className={styles.bedMonthSelect}
            placeholder="Select month"
            allowClear
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={monthOptions}
          />
        )}
      </header>

      <div className={styles.bedTableHorizontalScroll}>
        <div className={styles.bedTableHeader}>
          {BED_COLUMNS.map((col) => (
            <span
              key={col}
              className={`${styles.bedColLabel} ${
                col === "Total" ? styles.billingTableColEnd : ""
              }`}
            >
              {col}
            </span>
          ))}
        </div>

        <div className={styles.bedTableBody}>
          {filteredBeds.length === 0 ? (
            <p className={styles.bedEmpty}>No bed records found.</p>
          ) : (
            filteredBeds.map((bed, index) => (
              <div
                key={bed.id || `${bed.room_no}-${index}`}
                className={styles.bedTableRow}
              >
                <span className={styles.billingTableCellText}>
                  {dayjs(bed.start_date).format("D MMM YYYY")}
                </span>
                <span className={styles.bedCellMuted}>
                  {bed.end_date ? dayjs(bed.end_date).format("D MMM YYYY") : "—"}
                </span>
                <span className={styles.bedCellMuted}>{bed.total_bed_days ?? "—"}</span>
                <span className={styles.bedCellMuted}>{bed.room_no ?? "—"}</span>
                <span className={styles.bedCellMuted}>{bed.room_type ?? "—"}</span>
                <span>
                  {bed.status === "active" ? (
                    <span className={styles.statusBadge}>
                      <span className={styles.statusDot} />
                      Active
                    </span>
                  ) : (
                    <span className={styles.bedCellMuted}>Inactive</span>
                  )}
                </span>
                <span
                  className={`${styles.billingTableCellAmount} ${styles.billingTableColEnd}`}
                >
                  {formatToINR(bed.total_price)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default BedsIp;
